import {
  useGetDigitalBillIsEnabledForLocation,
  useIsFailedPaymentPromptEnabled,
  useIsOfflineModeEnabledForLocation,
  useIsQuickerLoginEnabledForLocation,
} from '@ordr-as/ordr-service-types/epos';
import { isValidLocationId, isValidNumericLocationId } from '../utils/locationId';

export function useQueryDigitalBillFeatureFlag(id?: number) {
  const { queryKey: _queryKey, ...result } = useGetDigitalBillIsEnabledForLocation(id ?? 0, {
    query: {
      enabled: isValidNumericLocationId(id),
      staleTime: 5 * 60 * 1000, // 5 min
    },
  });
  return result;
}

export function useQuickerLoginEnabled(id?: number) {
  const { queryKey: _queryKey, ...result } = useIsQuickerLoginEnabledForLocation(id ?? 0, {
    query: {
      enabled: isValidNumericLocationId(id),
      staleTime: 5 * 60 * 1000, // 5 min
    },
  });
  return result;
}

const OFFLINE_MODE_CACHE_KEY = 'favrit.epos.offlineModeEnabled';

function getCachedOfflineMode(locationId?: string): { enabled: boolean } | undefined {
  if (!locationId) return undefined;
  try {
    const cached = localStorage.getItem(`${OFFLINE_MODE_CACHE_KEY}.${locationId}`);
    return cached ? JSON.parse(cached) : undefined;
  } catch {
    return undefined;
  }
}

function setCachedOfflineMode(locationId: string, data: { enabled: boolean }) {
  try {
    localStorage.setItem(`${OFFLINE_MODE_CACHE_KEY}.${locationId}`, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
}

export function useQueryOfflineModeEnabled(locationId?: string) {
  const { queryKey: _queryKey, ...result } = useIsOfflineModeEnabledForLocation(locationId ?? '', {
    query: {
      enabled: isValidLocationId(locationId),
      initialData: getCachedOfflineMode(locationId),
      staleTime: 5 * 60 * 1000, // 5 min
    },
  });

  // Update cache when fresh data arrives
  if (result.data && locationId && !result.isPlaceholderData) {
    setCachedOfflineMode(locationId, result.data);
  }

  return result;
}

export function useQueryFailedPaymentPromptFeatureFlag(locationId?: string) {
  const { queryKey: _queryKey, ...result } = useIsFailedPaymentPromptEnabled(locationId ?? '', {
    query: {
      enabled: isValidLocationId(locationId),
      staleTime: 5 * 60 * 1000, // 5 min
    },
  });
  return result;
}
